<script lang="ts">
  import { ceiling, floor } from '../../core/engine';
  import { famousAt } from '../../core/famous';
  import { isPrime, isTwinPrime, nextPrime } from '../../core/numbers';
  import { fmt } from '../lib/format';
  import { game } from '../lib/game.svelte';

  /**
   * The Prime Radar: a ruler that slides under a fixed cursor.
   *
   * The ruler is built in pages: a 200-integer strip centred on `base`, which only moves
   * every 50 steps. A normal step changes nothing but the strip's transform (composited,
   * no repaint) and the one cell that just became visited.
   */
  const CELL = 34;
  const PAGE = 50;
  const HALF_STRIP = 100;

  const s = $derived(game.s);
  const pos = $derived(s.position);
  const top = $derived(ceiling(s));
  const bottom = $derived(floor(s));

  const base = $derived(Math.round(pos / PAGE) * PAGE);

  const cells = $derived.by(() => {
    const out = [];
    for (let n = base - HALF_STRIP; n <= base + HALF_STRIP; n++) {
      const prime = isPrime(n);
      out.push({
        n,
        prime,
        twin: prime && isTwinPrime(n),
        famous: famousAt(n).length > 0,
        beyond: n > top || n < bottom,
        label: prime || n % 5 === 0,
      });
    }
    return out;
  });

  const ahead = $derived(s.direction === 1 || pos > 2 ? nextPrime(pos, s.direction) : null);
  const reachable = $derived(ahead !== null && ahead <= top && ahead >= bottom);
</script>

<section class="radar" aria-label="Prime radar">
  <header>
    <span class="eyebrow">Prime radar</span>
    {#if ahead !== null && reachable}
      <span class="next">
        Next prime <strong class="num">{fmt(ahead)}</strong>
        <span class="dist num"
          >{fmt(Math.abs(ahead - pos))} {Math.abs(ahead - pos) === 1 ? 'step' : 'steps'}</span
        >
      </span>
    {:else}
      <span class="next muted">No primes this way</span>
    {/if}
  </header>

  <div class="window">
    <div class="track" style:transform="translateX({-pos * CELL - CELL / 2}px)">
      {#each cells as c (c.n)}
        <div
          class="cell"
          class:prime={c.prime}
          class:twin={c.twin}
          class:famous={c.famous}
          class:visited={c.n >= s.minVisited && c.n <= s.maxVisited}
          class:beyond={c.beyond}
          style:left="{c.n * CELL}px"
          style:width="{CELL}px"
        >
          <span class="tick" class:major={c.n % 10 === 0}></span>
          {#if c.famous && !c.prime}<span class="pip"></span>{/if}
          {#if c.label}<span class="lbl num">{c.n < 0 ? `−${-c.n}` : c.n}</span>{/if}
        </div>
      {/each}
    </div>
    <span class="cursor" aria-hidden="true"></span>
  </div>
</section>

<style>
  .radar {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
    padding-inline: 2px;
  }

  .next {
    font-size: var(--text-sm);
    color: var(--ink-2);
    display: inline-flex;
    gap: var(--space-2);
    align-items: baseline;
  }
  .next strong {
    color: var(--prime);
    font-weight: 700;
  }
  .dist {
    color: var(--ink-3);
    font-size: var(--text-xs);
  }
  .muted {
    color: var(--ink-3);
  }

  .window {
    position: relative;
    height: 64px;
    overflow: hidden;
    border-radius: var(--radius);
    background: var(--bg-sunken);
    border: 1px solid var(--line);
    mask-image: linear-gradient(90deg, transparent, #000 14%, #000 86%, transparent);
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 14%, #000 86%, transparent);
  }

  .track {
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    transition: transform var(--t) var(--ease-out);
    will-change: transform;
  }

  .cell {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 10px;
  }

  .tick {
    width: 2px;
    height: 10px;
    border-radius: 1px;
    background: var(--ink-3);
    opacity: 0.45;
  }
  .tick.major {
    height: 16px;
  }
  .visited .tick {
    opacity: 0.85;
  }

  .prime .tick {
    width: 3px;
    height: 26px;
    background: var(--prime);
    opacity: 1;
  }
  .twin .tick {
    background: var(--twin);
  }
  .prime:not(.visited) .tick {
    background: transparent;
    border: 1.5px solid var(--prime);
    width: 4px;
  }
  .twin:not(.visited) .tick {
    border-color: var(--twin);
  }

  .pip {
    position: absolute;
    top: 30px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--famous);
  }

  .lbl {
    position: absolute;
    bottom: 6px;
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--ink-3);
    white-space: nowrap;
  }
  .prime .lbl {
    color: var(--prime);
    font-weight: 700;
  }
  .twin .lbl {
    color: var(--twin);
  }

  .beyond {
    background: repeating-linear-gradient(
      135deg,
      transparent 0 5px,
      color-mix(in srgb, var(--ink-3) 18%, transparent) 5px 6px
    );
  }
  .beyond .tick,
  .beyond .lbl,
  .beyond .pip {
    opacity: 0.25;
  }

  .cursor {
    position: absolute;
    left: 50%;
    top: 0;
    height: 40px;
    border-radius: 0 0 2px 2px;
    width: 2px;
    margin-left: -1px;
    background: var(--accent);
    box-shadow: 0 0 10px color-mix(in srgb, var(--accent) 55%, transparent);
  }
  .cursor::before {
    content: '';
    position: absolute;
    top: 0;
    left: -4px;
    border: 5px solid transparent;
    border-top-color: var(--accent);
  }
</style>
